// Response shapes for the client-portal actions
// (quickback/features/client-portal/actions/*). All ids are cuid2 strings;
// startsAt/endsAt are NAIVE LOCAL wall-clock strings (no timezone) — format
// them with lib/format.ts, never `new Date(isoString)`.

export class APIError extends Error {
  constructor(
    public readonly kind:
      | "bad-request"
      | "unauthorized"
      | "forbidden"
      | "not-found"
      | "rate-limited"
      | "server-error"
      | "missing-auth-token",
    message: string,
    public readonly status?: number,
  ) {
    super(message);
    this.name = "APIError";
  }
}

// Lean theme tokens — mirror of the server's canonical type in
// quickback/features/events/lib/event-theme.ts. Server-sanitized: every
// value is a valid "#RRGGBB" by the time it reaches the client.
export interface ThemeModeTokens {
  accent?: string;
  backgroundTop?: string;
  backgroundBottom?: string;
  surface?: string;
  textPrimary?: string;
  textSecondary?: string;
}

export interface EventThemeTokens {
  light?: ThemeModeTokens;
  dark?: ThemeModeTokens;
}

export interface Brand {
  name: string | null;
  logoUrl: string | null;
  accentColor: string | null;
  // Brand theme merged with any per-event override server-side. Optional:
  // older deploys omit the field entirely.
  theme?: EventThemeTokens | null;
}

export interface InvitePreview {
  // Older deploys omit kind; treat absent as "client".
  kind?: "client" | "vendor";
  agencyName: string | null;
  // Present when kind === "client".
  clientName?: string;
  accessScope?: string; // "all-events" | "selected-events"
  // Present when kind === "vendor".
  vendorName?: string;
  memberStatus: string;
  brand: Brand | null;
}

export interface PortalClient {
  clientId: string;
  clientName: string;
  agencyName: string | null;
  accessScope: string;
  brand: Brand | null;
}

export interface PortalEventRow {
  eventId: string;
  name: string;
  slug: string | null;
  description: string | null;
  startsAt: string | null;
  endsAt: string | null;
  venueName: string | null;
  addressLocality: string | null;
  addressRegion: string | null;
  status: string;
  coverImageUrl: string | null;
  primaryColor: string | null;
  hostName: string | null;
  clientId: string | null;
  clientName: string | null;
  agencyName: string | null;
  company: Brand | null;
}

export interface PortalEventDetail {
  eventId: string;
  name: string;
  shortName: string | null;
  slug: string | null;
  description: string | null;
  startsAt: string | null;
  endsAt: string | null;
  ianaTimezone: string | null;
  venueName: string | null;
  streetAddress: string | null;
  addressLocality: string | null;
  addressRegion: string | null;
  postalCode: string | null;
  addressCountry: string | null;
  status: string;
  maxCapacity: number | null;
  rsvpDeadline: string | null;
  coverImageUrl: string | null;
  primaryColor: string | null;
  hostName: string | null;
  clientId: string | null;
}

export interface PortalEventDetailResponse {
  event: PortalEventDetail;
  clientName: string | null;
  agencyName: string | null;
  company: Brand | null;
}

export interface PortalGuest {
  guestId: string;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  nameAtInvite: string | null;
  emailAtInvite: string | null;
  titleAtInvite: string | null;
  companyAtInvite: string | null;
  status: string;
  guestCount: number | null;
  maxGuests: number | null;
  hostGuestId: string | null;
  relationshipToHost: string | null;
  dietaryNotes: string | null;
  specialRequests: string | null;
  sentAt: string | null;
  respondedAt: string | null;
  responseNote: string | null;
  checkedInAt: string | null;
  checkedOutAt: string | null;
}

export interface PortalReport {
  guests: {
    total: number;
    byStatus: Record<string, number>;
    confirmedHeadcount: number;
    checkedIn: number;
  };
  agenda: Array<{
    agendaItemId: string;
    name: string;
    startsAt: string | null;
    endsAt: string | null;
    confirmed: number;
    waitlisted: number;
    checkedIn: number;
  }>;
}

export interface PortalReportResponse {
  eventId: string;
  eventName: string;
  report: PortalReport;
}
